// This is scoped to a platform -- the `chartLegend` is a specific platform.
// Called by `calculateChartDataset` for each `chartLegend`
export const getDataSet = (
  checkList,
  field,
  chartLegend,
  aggregationAnalyticsList,
  datasetOptions = {},
  currentPeriodRange,
  comparisonPeriodRange,
  aggregateType
) => {
  const { currentPeriodData, comparisonPeriodData } = aggregationAnalyticsList;
  let currentTotalCount = 0;
  let comparisonTotalCount = 0;

  const sumOfArraysCurrentPeriod = sumArrays(
    getDatasetForSum({
      checkList,
      periodData: currentPeriodData,
      field,
    })
  );

  const chartData = getDatasetForChart({
    checkList,
    periodData: currentPeriodData,
    field,
    datasetOptions,
  });

  if (aggregateType === "sum") {
    currentTotalCount = sumOfArraysCurrentPeriod.reduce(
      (sum, x) => sum + (x || 0),
      0
    );
  } else {
    currentTotalCount =
      sumOfArraysCurrentPeriod.findLast((x) => x !== null) || 0;
  }

  const sumOfArraysComparisonPeriod = sumArrays(
    getDatasetForSum({
      checkList,
      periodData: comparisonPeriodData,
      field,
    })
  );

  if (aggregateType === "sum") {
    comparisonTotalCount = sumOfArraysComparisonPeriod.reduce(
      (sum, x) => sum + (x || 0),
      0
    );
  } else {
    comparisonTotalCount =
      sumOfArraysComparisonPeriod.findLast((x) => x !== null) || 0;
  }

  return {
    total: currentTotalCount,
    currentTotalCount,
    comparisonTotalCount,
    dataset: {
      key: chartLegend.key,
      label: chartLegend.label,
      data: chartData,
      backgroundColor: chartLegend.backgroundColor,
      borderColor: chartLegend.backgroundColor,
      spanGaps: true,
      ...datasetOptions,
    },
  };
};

const getDatasetForSum = ({ checkList, periodData, field }) => {
  const datasets = [];
  checkList
    .filter((x) => x.checked)
    .forEach((account) => {
      const accCurrentPeriodData = periodData
        .filter((x) => {
          let accountId = account.accountId;
          if (account.platformType === "FACEBOOK" && account.isGroup) {
            accountId = account.accountId.split("-")[0];
          }
          return x.accountId === accountId;
        })
        .sort((a, b) => (a.dateFrom > b.dateFrom ? 1 : -1));

      const tempDataSet = [];
      accCurrentPeriodData.forEach((acc, index) => {
        tempDataSet.push(acc[field]);
      });

      datasets.push(tempDataSet);
    });

  return datasets;
};

const getDatasetForChart = ({
  checkList,
  periodData,
  field,
  datasetOptions,
}) => {
  // Map<Date, Value>
  const data = new Map();
  checkList
    .filter((x) => x.checked)
    .forEach((account) => {
      periodData
        .filter((x) => {
          let accountId = account.accountId;
          if (account.platformType === "FACEBOOK" && account.isGroup) {
            accountId = account.accountId.split("-")[0];
          }
          return x.accountId === accountId;
        })
        .forEach((datum) => {
          const date = datum.dateFrom;
          const value = datum[field];

          if (data.has(date)) {
            // if we've got values, add them
            // if this data point has a value, but we didn't previously, set this data point's value
            // otherwise leave it null to represent sparsity
            // need null checks here because we do want to push 0 through
            const existingValue = data.get(date);
            let newValue = null;

            if (existingValue != null && value != null) {
              newValue = existingValue + value;
            } else if (existingValue == null && datasetOptions.fill) {
              newValue = existingValue;
            } else if (existingValue == null && value != null) {
              newValue = value;
            }

            data.set(date, newValue);
          } else {
            data.set(date, value);
          }
        });
    });

  const dataSet = [];
  data.forEach((value, date) => {
    dataSet.push({
      x: date,
      y: value,
    });
  });

  dataSet.sort((a, b) => {
    if (a.x < b.x) return -1;
    if (a.x > b.x) return 1;
    return 0;
  });

  return dataSet;
};

const sumArrays = (arrays) => {
  const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
  const result = Array.from({ length: n });
  return result.map((_, i) =>
    arrays
      .map((xs) => xs[i] || null)
      .reduce((sum, x) => {
        if (x === null && sum === null) {
          return null;
        } else if (x !== null && sum === null) {
          return x || 0;
        } else if (x === null && sum !== null) {
          return sum;
        } else {
          return sum + (x || 0);
        }
      }, null)
  );
};

export const calculateChartDataset = ({
  key,
  totalsByProvider,
  chartLegendList,
  accountCheckList,
  aggregationAnalyticsList,
  datasetOptions = {},
  currentPeriodRange,
  comparisonPeriodRange,
  aggregateType,
  excludeMetrics,
}) => {
  let datasets = [];
  const totals = {
    totalCount: 0,
    currentTotalCount: 0,
    comparisonTotalCount: 0,
    totalPercent: 0,
  };
  chartLegendList
    .filter(
      (legend) =>
        !excludeMetrics.find(
          (item) =>
            legend.key === item.key &&
            item.metrics.find((metric) => metric === key)
        )
    )
    .forEach((chartLegend) => {
      const res = getDataSet(
        accountCheckList[chartLegend.key],
        key,
        chartLegend,
        aggregationAnalyticsList,
        datasetOptions,
        currentPeriodRange,
        comparisonPeriodRange,
        aggregateType
      );
      totals.totalCount += res.currentTotalCount;
      totals.currentTotalCount += res.currentTotalCount;
      totals.comparisonTotalCount += res.comparisonTotalCount;

      if (aggregateType === "sum") {
        totalsByProvider[chartLegend.key][key].currentTotalCount =
          res.currentTotalCount;
        totalsByProvider[chartLegend.key][key].comparisonTotalCount =
          res.comparisonTotalCount;
      } else {
        totalsByProvider[chartLegend.key][key].currentTotalCount =
          res.currentTotalCount;
        totalsByProvider[chartLegend.key][key].comparisonTotalCount =
          res.comparisonTotalCount;
      }

      totalsByProvider[chartLegend.key].percentList[key] = -getPercentageChange(
        res.comparisonTotalCount,
        res.currentTotalCount
      ).toFixed(2);

      datasets.push(res.dataset);
    });

  totals.totalPercent = -getPercentageChange(
    totals.comparisonTotalCount,
    totals.totalCount
  ).toFixed(2);

  return {
    datasets,
    totals,
    totalsByProvider,
  };
};

export const getPercentageChange = (oldNumber, newNumber) => {
  const decreaseValue = oldNumber - newNumber;

  return (decreaseValue / (oldNumber || 1)) * 100;
};

export const defaultChartLegendList = [
  {
    key: "facebook",
    label: "Facebook",
    backgroundColor: "#2572BF",
    borderColor: "#2572BF",
    show: true,
  },
  {
    key: "instagram",
    label: "Instagram",
    backgroundColor: "#dd5571",
    borderColor: "#dd5571",
    show: true,
  },
  {
    key: "threads",
    label: "Threads",
    backgroundColor: "#80ADE1",
    borderColor: "#80ADE1",
    show: true,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    backgroundColor: "#A7E7CD",
    borderColor: "#A7E7CD",
    show: true,
  },
  {
    key: "tiktok",
    label: "TikTok",
    backgroundColor: "#D690AB",
    borderColor: "#D690AB",
    show: true,
  },
  {
    key: "pinterest",
    label: "Pinterest",
    backgroundColor: "#F8D377",
    borderColor: "#F8D377",
    show: true,
  },
  {
    key: "youtube",
    label: "YouTube",
    backgroundColor: "#E8A666",
    borderColor: "#E8A666",
    show: true,
  },
  {
    key: "gmb",
    label: "Google Business Profile",
    backgroundColor: "#8F533F",
    borderColor: "#8F533F",
    show: true,
  },
];

export const monthLabelList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultTotals = {
  totalCount: 0,
  currentTotalCount: 0,
  comparisonTotalCount: 0,
  totalPercent: 0,
};

export const getDefaultChartData = (keyList, displayedProviders) => {
  const data = {
    totals: {},
    providerTotals: {},
  };

  keyList.forEach((key) => (data.totals[key] = { ...defaultTotals }));

  defaultChartLegendList
    .filter(
      (legend) =>
        !!Object.keys(displayedProviders).find(
          (provider) => legend.key === provider
        )
    )
    .forEach((legend) => {
      data.providerTotals[legend.key] = {
        percentList: {},
      };
      keyList.forEach((key) => {
        data.providerTotals[legend.key][key] = {
          currentTotalCount: 0,
          comparisonTotalCount: 0,
        };
      });
      keyList.forEach((key) => {
        data.providerTotals[legend.key].percentList[key] = 0;
      });
    });

  return data;
};

export const defaultChartOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      stacked: true,
      title: {
        display: false,
      },
      min: 0,
    },
    x: {
      type: "time",
      time: {
        unit: "day",
        parser: "YYYY-MM-DD",
        tooltipFormat: "ll",
        displayFormats: {
          day: "MM/DD/YYYY",
        },
      },
    },
  },
  interaction: {
    intersect: false,
    mode: "x",
  },
  bezierCurve: false,
  elements: {
    point: {
      radius: 0,
    },
  },
  maintainAspectRatio: false,
};

export const getSelectedPlatforms = (accountCheckList) => {
  return Object.keys(accountCheckList).reduce((filtered, platformName) => {
    const filteredArray = accountCheckList[platformName].filter(
      (item) => item.checked
    );

    if (filteredArray.length > 0) {
      filtered.push(platformName);
    }
    return filtered;
  }, []);
};

export const formatPercentage = (value) => {
  return `${parseFloat((value * 100).toFixed(2))}%`
};
